import { useState } from 'react'

interface UseReversedFiltersParams {
  /** Имя поля фильтра -> ключ, которым это поле называется в reversed_filters на бэке. */
  keys: Record<string, string>
  /** Применённое значение из стора — строка через запятую. */
  applied?: string
  /** Значение из урла, если страницу открыли по ссылке. */
  fromUrl?: string
  /** Применить фильтр с этим набором обратных полей. */
  onApply: (reversed: string[]) => void
}

const parse = (value?: string) => (value ? value.split(',') : [])

const toggleKey = (reversed: string[], key: string) =>
  reversed.includes(key) ? reversed.filter((filter) => filter !== key) : [...reversed, key]

/**
 * Обратная фильтрация (is / is not) для полей фильтра.
 *
 * Черновик правят чекбоксы в дропе — он уходит в стор только по Apply, а если дроп закрыли
 * без Apply, откатывается к применённому (restoreReversedFilters).
 * Чипы показывают уже применённое состояние и применяются сразу по клику.
 */
export function useReversedFilters({ keys, applied, fromUrl, onApply }: UseReversedFiltersParams) {
  const [reversedFilters, setReversedFilters] = useState<string[]>(() => parse(fromUrl ?? applied))
  const appliedReversedFilters = parse(applied)

  const isReversed = (field: string) => reversedFilters.includes(keys[field])

  const isReversedApplied = (field: string) => appliedReversedFilters.includes(keys[field])

  const toggleReversedFilter = (field: string) => {
    setReversedFilters((prev) => toggleKey(prev, keys[field]))
  }

  const toggleReversedFilterAndApply = (field: string) => {
    const key = keys[field]
    const next = toggleKey(appliedReversedFilters, key)

    setReversedFilters((prev) => {
      const rest = prev.filter((filter) => filter !== key)

      return next.includes(key) ? [...rest, key] : rest
    })
    onApply(next)
  }

  /** Убирает поле из обратной фильтрации и отдаёт набор, с которым надо применить фильтр. */
  const clearReversedFilter = (field: string) => {
    const key = keys[field]

    setReversedFilters((prev) => prev.filter((filter) => filter !== key))

    return appliedReversedFilters.filter((filter) => filter !== key)
  }

  const resetReversedFilters = () => setReversedFilters([])

  const restoreReversedFilters = () => setReversedFilters(appliedReversedFilters)

  return {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  }
}
